// ═══════════════════════════════════════════════════════════════════════════════
// Pure 223 Intelligence  — 360-IF Core Intelligence Engine
// COMPREHENSIVE VERSION - Expanded intents, entities, patterns for educational use
//
// Features:
//   • 25+ discriminative intent types with contrastive learning
//   • 12 entity types (PERSON, ORG, DATE, NUMBER, CONCEPT, LOCATION, etc.)
//   • 15 response pattern types
//   • 10+ handler patterns for complex queries
//   • Structural Q&A generation
//   • Writing-specific analysis patterns
//   • Robust error handling throughout
//
// All functions are module-level to avoid 'this' binding issues with bundlers.
// ═══════════════════════════════════════════════════════════════════════════════

import { C } from './constants.js';

// ═══════════════════════════════════════════════════════════════════════════════
// MODULE-LEVEL STATE
// ═══════════════════════════════════════════════════════════════════════════════

let intentVectors = new Map();
let entityVectors = new Map();
let responsePatterns = new Map();
let intentNegatives = new Map();
let handlerPatterns = new Map();
let handlerNegatives = new Map();
let writingPatterns = new Map();
let initialized = false;

let structuralEmbeddings = {
  documentType: null,
  hierarchy: [],
  paragraphTypes: new Map(),
  readingLevel: null,
  keyPhrases: [],
  topicClusters: []
};

let trainingExamples = {
  intents: [],
  entities: [],
  responses: [],
  evidence: [],
  structural: [],
  writing: [],
  handlers: []
};

// ═══════════════════════════════════════════════════════════════════════════════
// SAFE INITIALIZATION CHECK
// ═══════════════════════════════════════════════════════════════════════════════

function ensureInitialized() {
  if (!initialized) {
    intentVectors = new Map();
    entityVectors = new Map();
    responsePatterns = new Map();
    intentNegatives = new Map();
    handlerPatterns = new Map();
    handlerNegatives = new Map();
    writingPatterns = new Map();
    initialized = true;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

function averageVectors(vectors) {
  try {
    if (!vectors || vectors.length === 0) return null;
    const validVectors = vectors.filter(v => v && Array.isArray(v) && v.length > 0);
    if (validVectors.length === 0) return null;
    
    const dim = validVectors[0].length;
    const avg = new Array(dim).fill(0);
    
    for (const vec of validVectors) {
      for (let i = 0; i < Math.min(vec.length, dim); i++) {
        avg[i] += vec[i] / validVectors.length;
      }
    }
    return avg;
  } catch (e) {
    console.warn('[Pure223] averageVectors error:', e.message);
    return null;
  }
}

function weightedAverageVectors(vectors, weights) {
  try {
    if (!vectors || vectors.length === 0) return null;
    const validVectors = vectors.filter(v => v && Array.isArray(v) && v.length > 0);
    if (validVectors.length === 0) return null;
    
    const dim = validVectors[0].length;
    const avg = new Array(dim).fill(0);
    let totalWeight = 0;
    
    for (let j = 0; j < validVectors.length; j++) {
      const vec = validVectors[j];
      const w = weights?.[j] || 1;
      totalWeight += w;
      for (let i = 0; i < Math.min(vec.length, dim); i++) {
        avg[i] += vec[i] * w;
      }
    }
    
    if (totalWeight > 0) {
      for (let i = 0; i < dim; i++) {
        avg[i] /= totalWeight;
      }
    }
    return avg;
  } catch (e) {
    console.warn('[Pure223] weightedAverageVectors error:', e.message);
    return null;
  }
}

function cosineSimilarity(a, b) {
  try {
    if (!a || !b || !Array.isArray(a) || !Array.isArray(b)) return 0;
    if (a.length !== b.length) return 0;
    
    let dot = 0, magA = 0, magB = 0;
    for (let i = 0; i < a.length; i++) {
      const ai = a[i] || 0;
      const bi = b[i] || 0;
      dot += ai * bi;
      magA += ai * ai;
      magB += bi * bi;
    }
    
    if (magA === 0 || magB === 0) return 0;
    return dot / (Math.sqrt(magA) * Math.sqrt(magB));
  } catch (e) {
    return 0;
  }
}

function cosineSim(a, b) {
  return cosineSimilarity(a, b);
}

function normalizeVector(vec) {
  try {
    if (!vec || vec.length === 0) return vec;
    let mag = 0;
    for (let i = 0; i < vec.length; i++) {
      mag += (vec[i] || 0) ** 2;
    }
    mag = Math.sqrt(mag);
    if (mag === 0) return vec;
    return vec.map(v => (v || 0) / mag);
  } catch (e) {
    return vec;
  }
}

function euclideanDistance(a, b) {
  try {
    if (!a || !b || a.length !== b.length) return Infinity;
    let sum = 0;
    for (let i = 0; i < a.length; i++) {
      const diff = (a[i] || 0) - (b[i] || 0);
      sum += diff * diff;
    }
    return Math.sqrt(sum);
  } catch (e) {
    return Infinity;
  }
}

function safeEncode(encodeFn, forwardFn, text) {
  try {
    if (!text || typeof text !== 'string' || text.trim().length === 0) return null;
    if (typeof encodeFn !== 'function' || typeof forwardFn !== 'function') return null;
    
    const encoded = encodeFn(text);
    if (!encoded) return null;
    
    const result = forwardFn(encoded);
    if (!result || !result.h2) return null;
    
    return Array.from(result.h2);
  } catch (e) {
    return null;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTENT TRAINING DATA
// ═══════════════════════════════════════════════════════════════════════════════

const INTENT_TRAINING_DATA = {
  definition: {
    examples: ['what is the definition of', 'what does this term mean', 'define the concept', 'explain what means', 'what is meant by', 'how is defined'],
    anchors: ['definition', 'define', 'meaning', 'meant by', 'what is'],
    negatives: ['list', 'compare', 'why']
  },
  acronym: {
    examples: ['what does stand for', 'expand this acronym', 'full form of', 'abbreviation meaning'],
    anchors: ['stand for', 'acronym', 'abbreviation', 'expand', 'full form'],
    negatives: ['summarize', 'compare']
  },
  explanation: {
    examples: ['explain this concept', 'can you explain', 'help me understand', 'break this down', 'explain in simple terms'],
    anchors: ['explain', 'clarify', 'understand', 'break down', 'simple terms'],
    negatives: ['list', 'compare', 'when']
  },
  list: {
    examples: ['list all the items', 'enumerate the points', 'show me all', 'what are all the', 'give me a list'],
    anchors: ['list', 'enumerate', 'all the', 'every', 'itemize', 'show all'],
    negatives: ['define', 'why', 'compare']
  },
  count: {
    examples: ['how many are there', 'what is the count', 'total number of', 'how much does', 'what percentage'],
    anchors: ['how many', 'count', 'number of', 'how much', 'percentage', 'total'],
    negatives: ['why', 'define', 'compare']
  },
  comparison: {
    examples: ['compare X and Y', 'difference between', 'how does differ from', 'contrast these', 'versus'],
    anchors: ['compare', 'difference', 'versus', 'vs', 'contrast', 'differ', 'similarities'],
    negatives: ['define', 'list', 'summarize']
  },
  cause: {
    examples: ['why does this happen', 'what causes', 'the reason for', 'what leads to', 'because of what'],
    anchors: ['why', 'cause', 'reason', 'because', 'effect', 'result', 'leads to'],
    negatives: ['what is', 'list', 'when']
  },
  process: {
    examples: ['how does this work', 'explain the process', 'what are the steps', 'walk me through', 'how do you'],
    anchors: ['how does', 'process', 'steps', 'procedure', 'how to', 'method'],
    negatives: ['why', 'what is', 'compare']
  },
  time: {
    examples: ['when did this happen', 'what date', 'timeline of events', 'when is the deadline', 'how long'],
    anchors: ['when', 'date', 'timeline', 'deadline', 'duration', 'how long'],
    negatives: ['where', 'what is', 'why']
  },
  location: {
    examples: ['where is this located', 'in what place', 'geographic location', 'where can I find'],
    anchors: ['where', 'location', 'place', 'located', 'address', 'setting'],
    negatives: ['when', 'what is', 'why']
  },
  person: {
    examples: ['who is mentioned', 'who wrote this', 'identify the author', 'people involved', 'who said'],
    anchors: ['who', 'person', 'people', 'author', 'individual', 'character'],
    negatives: ['what is', 'where', 'when']
  },
  summary: {
    examples: ['summarize this document', 'give me an overview', 'main points', 'brief summary', 'key takeaways'],
    anchors: ['summarize', 'summary', 'overview', 'main points', 'takeaway', 'gist'],
    negatives: ['list all', 'compare', 'why']
  },
  evidence: {
    examples: ['what evidence supports', 'quote from the document', 'cite the source', 'proof of this claim'],
    anchors: ['evidence', 'quote', 'cite', 'source', 'proof', 'reference', 'support'],
    negatives: ['summarize', 'why', 'compare']
  },
  example: {
    examples: ['give me an example', 'show an example', 'what is an example of', 'illustrate with example'],
    anchors: ['example', 'illustrate', 'demonstrate', 'instance', 'such as'],
    negatives: ['define', 'summarize', 'list all']
  },
  opinion: {
    examples: ['what is the opinion', 'what does the author think', 'what is the viewpoint', 'what stance'],
    anchors: ['opinion', 'think', 'viewpoint', 'position', 'stance', 'belief', 'perspective'],
    negatives: ['fact', 'evidence', 'define']
  },
  theme: {
    examples: ['what is the theme', 'main theme of', 'central idea', 'underlying message', 'moral of'],
    anchors: ['theme', 'central idea', 'message', 'moral', 'lesson', 'underlying'],
    negatives: ['plot', 'character', 'setting']
  },
  plot: {
    examples: ['what is the plot', 'what happens in the story', 'storyline', 'events of the story'],
    anchors: ['plot', 'story', 'storyline', 'events', 'conflict', 'happens'],
    negatives: ['theme', 'character', 'setting']
  },
  character: {
    examples: ['describe the character', 'who is the protagonist', 'character traits', 'main character'],
    anchors: ['character', 'protagonist', 'antagonist', 'traits', 'personality'],
    negatives: ['plot', 'setting', 'theme']
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// ENTITY PATTERNS
// ═══════════════════════════════════════════════════════════════════════════════

const ENTITY_PATTERNS = {
  PERSON: {
    patterns: [/\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\b/g],
    keywords: ['person', 'author', 'writer', 'speaker', 'narrator', 'character']
  },
  ORGANIZATION: {
    patterns: [/\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+){2,})\b/g, /\b([A-Z]{2,})\b/g],
    keywords: ['company', 'organization', 'institution', 'agency', 'corporation']
  },
  DATE: {
    patterns: [/\b(\d{4})\b/g, /\b((?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2})/gi],
    keywords: ['date', 'year', 'month', 'day', 'century']
  },
  TIME: {
    patterns: [/\b(\d{1,2}:\d{2}(?::\d{2})?\s*(?:AM|PM|am|pm)?)\b/g],
    keywords: ['time', 'hour', 'minute', 'second']
  },
  NUMBER: {
    patterns: [/\b(\d+(?:,\d{3})*(?:\.\d+)?)\b/g, /\b(\d+(?:\.\d+)?%)\b/g],
    keywords: ['number', 'amount', 'quantity', 'percentage']
  },
  MONEY: {
    patterns: [/(\$\d+(?:,\d{3})*(?:\.\d{2})?)/g],
    keywords: ['price', 'cost', 'money', 'dollar', 'payment']
  },
  LOCATION: {
    patterns: [/\b(?:in|at|from|to)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\b/g],
    keywords: ['location', 'place', 'city', 'country', 'state', 'region']
  },
  CONCEPT: {
    patterns: [],
    keywords: ['concept', 'idea', 'theory', 'principle', 'philosophy']
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// RESPONSE PATTERNS
// ═══════════════════════════════════════════════════════════════════════════════

const RESPONSE_PATTERNS = {
  definitional: { indicators: [/\bis\b/, /\bare\b/, /\bmeans\b/, /\brefers to\b/], maxLength: 200 },
  enumerative: { indicators: [/\d+\.\s/, /\bfirst\b/i, /\bsecond\b/i, /•/, /-\s+\w/], maxLength: null },
  explanatory: { indicators: [/because/i, /therefore/i, /thus/i, /as a result/i], maxLength: null },
  comparative: { indicators: [/compar/i, /differ/i, /contrast/i, /whereas/i, /similar/i], maxLength: null },
  quantitative: { indicators: [/\d+%/, /\d+\.\d+/, /percent/i, /total/i], maxLength: null },
  quotational: { indicators: [/[""]/, /according to/i, /stated/i, /said/i], maxLength: null },
  procedural: { indicators: [/step/i, /first/i, /then/i, /next/i, /finally/i], maxLength: null },
  narrative: { indicators: [/once upon/i, /began/i, /happened/i, /story/i], maxLength: null },
  argumentative: { indicators: [/argue/i, /claim/i, /believe/i, /opinion/i], maxLength: null }
};

// ═══════════════════════════════════════════════════════════════════════════════
// HANDLER PATTERNS
// ═══════════════════════════════════════════════════════════════════════════════

const HANDLER_PATTERNS = {
  deep_analysis: {
    examples: ['deep understanding analysis', 'comprehensive analysis', 'in-depth analysis'],
    anchors: ['deep understanding', 'comprehensive', 'in-depth', 'thorough'],
    negatives: ['summarize', 'quick', 'brief'],
    responseType: 'full_analysis'
  },
  counterargument: {
    examples: ['what are the counterarguments', 'argue against', 'opposing viewpoint'],
    anchors: ['counterargument', 'oppose', 'against', 'critique'],
    negatives: ['support', 'agree'],
    responseType: 'counterargument_list'
  },
  synthesis: {
    examples: ['synthesize these ideas', 'combine these concepts', 'integrate information'],
    anchors: ['synthesize', 'combine', 'integrate', 'unified'],
    negatives: ['separate', 'distinguish'],
    responseType: 'synthesis'
  },
  inference: {
    examples: ['what can we infer', 'what does this imply', 'read between the lines'],
    anchors: ['infer', 'imply', 'suggest', 'implicit'],
    negatives: ['explicit', 'stated', 'direct'],
    responseType: 'inference'
  },
  prediction: {
    examples: ['what will happen next', 'predict the outcome', 'what might occur'],
    anchors: ['predict', 'will happen', 'might occur', 'future', 'forecast'],
    negatives: ['past', 'history', 'happened'],
    responseType: 'prediction'
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// WRITING PATTERNS
// ═══════════════════════════════════════════════════════════════════════════════

const WRITING_PATTERNS = {
  claim: { indicators: [/\bi (?:think|believe|feel)\b/i, /\bin my opinion\b/i, /\bshould\b/i], weight: 2 },
  reasoning: { indicators: [/\bbecause\b/i, /\btherefore\b/i, /\bthis shows\b/i], weight: 2 },
  evidence: { indicators: [/\bfor example\b/i, /\baccording to\b/i, /\bthe text states\b/i], weight: 2 },
  coherence: { indicators: [/\bfirst\b/i, /\bhowever\b/i, /\bin addition\b/i, /\bfinally\b/i], weight: 1 },
  conclusion: { indicators: [/\bin conclusion\b/i, /\bto summarize\b/i, /\boverall\b/i], weight: 1 }
};

// ═══════════════════════════════════════════════════════════════════════════════
// STRUCTURAL Q&A GENERATION
// ═══════════════════════════════════════════════════════════════════════════════

function generateStructuralQA(structure, sourceContent) {
  const qa = [];
  
  try {
    if (!structure) return qa;

    if (structure.totalParagraphs) {
      qa.push({ q: 'How many paragraphs does this document have?', a: `This document has ${structure.totalParagraphs} paragraphs.`, type: 'structural_count' });
    }
    if (structure.totalSentences) {
      qa.push({ q: 'How many sentences are in this document?', a: `This document contains ${structure.totalSentences} sentences.`, type: 'structural_count' });
    }
    if (structure.totalWords) {
      qa.push({ q: 'What is the word count?', a: `This document has ${structure.totalWords} words.`, type: 'structural_count' });
    }
    if (structure.documentType?.type) {
      const t = structure.documentType.type.replace(/_/g, ' ');
      qa.push({ q: 'What type of document is this?', a: `This is a ${t}.`, type: 'document_type' });
    }
    if (structure.readingLevel?.gradeLevel) {
      qa.push({ q: 'What is the reading level?', a: `The reading level is Grade ${structure.readingLevel.gradeLevel}.`, type: 'reading_level' });
    }

    // Content summary
    if (sourceContent && sourceContent.length > 100) {
      const paragraphs = sourceContent.split(/\n\n+/).filter(p => p.trim().length > 50);
      for (const para of paragraphs) {
        if (/please contact|sincerely|regards/i.test(para)) continue;
        let summary = para.trim();
        const sents = summary.split(/[.!?]+/).filter(s => s.trim().length > 10);
        if (sents.length > 2) summary = sents.slice(0, 2).join('. ').trim() + '.';
        qa.push({ q: 'What is this document about?', a: summary, type: 'content_summary' });
        qa.push({ q: 'What is the main idea?', a: summary, type: 'content_summary' });
        break;
      }
    }
  } catch (e) {
    console.warn('[Pure223] generateStructuralQA error:', e.message);
  }

  return qa;
}

// ═══════════════════════════════════════════════════════════════════════════════
// LITERARY Q&A GENERATION
// ═══════════════════════════════════════════════════════════════════════════════

function generateLiteraryQA(content, docType) {
  const qa = [];
  
  try {
    if (!content || content.length < 100) return qa;

    const commonWords = new Set(['The', 'This', 'That', 'What', 'When', 'Where', 'Why', 'How', 'There', 'Here', 'And', 'But', 'For', 'With', 'From', 'First', 'Second', 'Third', 'Last', 'Next', 'New', 'Old', 'Good', 'Great']);

    const namePattern = /\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)\b/g;
    const nameCounts = {};
    let match;

    while ((match = namePattern.exec(content)) !== null) {
      const name = match[1].trim();
      if (commonWords.has(name) || name.length < 3) continue;
      nameCounts[name] = (nameCounts[name] || 0) + 1;
    }

    const significantNames = Object.entries(nameCounts)
      .filter(([_, count]) => count >= 2)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);

    for (const [name] of significantNames) {
      const sentences = content.split(/[.!?]+/).filter(s => s.includes(name) && s.trim().length > 20);
      if (sentences.length === 0) continue;
      const bestSent = sentences[0].trim();
      qa.push({ q: `Who is ${name}?`, a: bestSent, type: 'entity_identity' });
      qa.push({ q: `Tell me about ${name}.`, a: bestSent, type: 'entity_description' });
    }
  } catch (e) {
    console.warn('[Pure223] generateLiteraryQA error:', e.message);
  }

  return qa;
}

// ═══════════════════════════════════════════════════════════════════════════════
// LEARN STRUCTURAL EMBEDDINGS
// ═══════════════════════════════════════════════════════════════════════════════

function learnStructuralEmbeddings(structure, forwardFn, encodeFn) {
  ensureInitialized();
  if (!structure || typeof forwardFn !== 'function' || typeof encodeFn !== 'function') return 0;
  
  let learned = 0;

  try {
    if (structure.documentType?.type) {
      const vec = safeEncode(encodeFn, forwardFn, `document type ${structure.documentType.type}`);
      if (vec) {
        structuralEmbeddings.documentType = {
          type: structure.documentType.type,
          confidence: structure.documentType.confidence || 1,
          vector: vec
        };
        learned++;
      }
    }

    if (structure.hierarchy?.length > 0) {
      structuralEmbeddings.hierarchy = [];
      for (const element of structure.hierarchy.slice(0, 50)) {
        const text = `${element.type} ${element.number || ''} ${element.title || ''}`.trim();
        const vec = safeEncode(encodeFn, forwardFn, text);
        if (vec) {
          structuralEmbeddings.hierarchy.push({ element, vector: vec });
          learned++;
        }
      }
    }

    if (structure.readingLevel?.gradeLevel) {
      const vec = safeEncode(encodeFn, forwardFn, `reading level grade ${structure.readingLevel.gradeLevel}`);
      if (vec) {
        structuralEmbeddings.readingLevel = { gradeLevel: structure.readingLevel.gradeLevel, vector: vec };
        learned++;
      }
    }
  } catch (err) {
    console.warn('[Pure223] learnStructuralEmbeddings error:', err.message);
  }

  return learned;
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTENT CLASSIFICATION
// ═══════════════════════════════════════════════════════════════════════════════

function classifyIntent(queryVector, queryText) {
  ensureInitialized();
  
  const defaultResult = { intent: 'general', confidence: 0.5, discrimination: 0, allScores: [] };
  
  try {
    if (!queryVector || !Array.isArray(queryVector) || intentVectors.size === 0) {
      return defaultResult;
    }

    const qLow = (queryText || '').toLowerCase();
    const scores = [];

    for (const [intent, centroid] of intentVectors) {
      if (!centroid) continue;
      
      let sim = cosineSimilarity(queryVector, centroid);

      // Negative penalty
      const negatives = intentNegatives.get(intent);
      if (negatives && negatives.length > 0) {
        let maxNegSim = 0;
        for (const neg of negatives) {
          if (neg) maxNegSim = Math.max(maxNegSim, cosineSimilarity(queryVector, neg));
        }
        sim -= maxNegSim * 0.3;
      }

      // Anchor bonus
      const intentData = INTENT_TRAINING_DATA[intent];
      if (intentData?.anchors && qLow) {
        for (const anchor of intentData.anchors) {
          if (qLow.includes(anchor)) {
            sim += 0.2;
            break;
          }
        }
      }

      scores.push({ intent, finalScore: Math.max(0, sim) });
    }

    if (scores.length === 0) return defaultResult;

    scores.sort((a, b) => b.finalScore - a.finalScore);
    const best = scores[0];
    const second = scores[1] || { finalScore: 0 };
    const disc = best.finalScore - second.finalScore;

    let confidence = best.finalScore;
    if (disc < 0.05) confidence *= 0.7;
    else if (disc < 0.1) confidence *= 0.85;

    return {
      intent: best.intent,
      confidence: Math.max(0, Math.min(1, confidence)),
      discrimination: disc,
      allScores: scores.slice(0, 5).map(s => ({ intent: s.intent, score: s.finalScore }))
    };
  } catch (e) {
    console.warn('[Pure223] classifyIntent error:', e.message);
    return defaultResult;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// LEARN INTENTS
// ═══════════════════════════════════════════════════════════════════════════════

function learnIntents(qaPairs, forwardFn, encodeFn) {
  ensureInitialized();
  
  if (typeof forwardFn !== 'function' || typeof encodeFn !== 'function') {
    console.warn('[Pure223] learnIntents: invalid functions provided');
    return 0;
  }

  try {
    for (const [intent, data] of Object.entries(INTENT_TRAINING_DATA)) {
      const positiveVectors = [];
      const weights = [];

      for (const example of data.examples) {
        const vec = safeEncode(encodeFn, forwardFn, example);
        if (!vec) continue;

        const isAnchor = data.anchors.some(a => example.toLowerCase().includes(a));
        positiveVectors.push(vec);
        weights.push(isAnchor ? 2 : 1);
        trainingExamples.intents.push({ query: example, intent, vector: vec });
      }

      if (positiveVectors.length > 0) {
        const centroid = weightedAverageVectors(positiveVectors, weights);
        if (centroid) intentVectors.set(intent, centroid);
      }

      // Learn negative examples
      const negativeVectors = [];
      for (const negWord of (data.negatives || [])) {
        const vec = safeEncode(encodeFn, forwardFn, negWord + ' something');
        if (vec) negativeVectors.push(vec);
      }
      if (negativeVectors.length > 0) {
        intentNegatives.set(intent, negativeVectors);
      }
    }

    // Learn from Q&A pairs too
    if (qaPairs && Array.isArray(qaPairs)) {
      for (const qa of qaPairs.slice(0, 200)) {
        if (!qa || !qa.q) continue;
        const vec = safeEncode(encodeFn, forwardFn, qa.q);
        if (!vec) continue;
        
        const detected = classifyIntent(vec, qa.q);
        trainingExamples.intents.push({
          query: qa.q,
          intent: detected.intent,
          vector: vec,
          confidence: detected.confidence
        });
      }
    }

    console.log(`[Pure223] Learned ${intentVectors.size} intent types`);
    return intentVectors.size;
  } catch (err) {
    console.warn('[Pure223] learnIntents error:', err.message);
    return 0;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// LEARN ENTITIES
// ═══════════════════════════════════════════════════════════════════════════════

function learnEntities(chunks, forwardFn, encodeFn) {
  ensureInitialized();
  
  if (typeof forwardFn !== 'function' || typeof encodeFn !== 'function' || !chunks) return 0;

  const entityExamples = {};
  for (const type of Object.keys(ENTITY_PATTERNS)) {
    entityExamples[type] = [];
  }

  try {
    for (const chunk of chunks.slice(0, 100)) {
      const text = typeof chunk === 'string' ? chunk : (chunk?.text || chunk?.content || '');
      if (!text || typeof text !== 'string') continue;

      for (const [type, config] of Object.entries(ENTITY_PATTERNS)) {
        for (const pattern of (config.patterns || [])) {
          try {
            const regex = new RegExp(pattern.source, pattern.flags);
            let match;
            let count = 0;
            while ((match = regex.exec(text)) !== null && count < 5) {
              const entity = match[1] || match[0];
              if (entity && entity.length > 1 && entity.length < 100) {
                const vec = safeEncode(encodeFn, forwardFn, entity);
                if (vec) {
                  entityExamples[type].push(vec);
                  trainingExamples.entities.push({ text: entity, type, vector: vec });
                }
              }
              count++;
            }
          } catch (e) {
            // Skip invalid patterns
          }
        }

        for (const keyword of (config.keywords || [])) {
          if (text.toLowerCase().includes(keyword)) {
            const vec = safeEncode(encodeFn, forwardFn, keyword);
            if (vec) entityExamples[type].push(vec);
          }
        }
      }
    }

    for (const [type, vectors] of Object.entries(entityExamples)) {
      if (vectors.length > 0) {
        const centroid = averageVectors(vectors);
        if (centroid) entityVectors.set(type, centroid);
      }
    }

    console.log(`[Pure223] Learned ${entityVectors.size} entity types`);
    return entityVectors.size;
  } catch (err) {
    console.warn('[Pure223] learnEntities error:', err.message);
    return 0;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// LEARN RESPONSE PATTERNS
// ═══════════════════════════════════════════════════════════════════════════════

function learnResponsePatterns(qaPairs, forwardFn, encodeFn) {
  ensureInitialized();
  
  if (typeof forwardFn !== 'function' || typeof encodeFn !== 'function' || !qaPairs) return 0;

  const patternExamples = {};
  for (const type of Object.keys(RESPONSE_PATTERNS)) {
    patternExamples[type] = [];
  }

  try {
    for (const qa of qaPairs) {
      if (!qa || !qa.a) continue;
      const a = qa.a;
      const vec = safeEncode(encodeFn, forwardFn, a);
      if (!vec) continue;

      for (const [patternType, config] of Object.entries(RESPONSE_PATTERNS)) {
        if (config.maxLength && a.length > config.maxLength) continue;
        
        let matches = false;
        for (const indicator of (config.indicators || [])) {
          try {
            if (indicator.test && indicator.test(a)) {
              matches = true;
              break;
            }
          } catch (e) {
            // Skip invalid indicators
          }
        }

        if (matches) {
          patternExamples[patternType].push(vec);
        }
      }

      trainingExamples.responses.push({
        query: qa.q,
        response: a.substring(0, 300),
        vector: vec
      });
    }

    for (const [pattern, vectors] of Object.entries(patternExamples)) {
      if (vectors.length >= 2) {
        const centroid = averageVectors(vectors);
        if (centroid) responsePatterns.set(pattern, centroid);
      }
    }

    console.log(`[Pure223] Learned ${responsePatterns.size} response patterns`);
    return responsePatterns.size;
  } catch (err) {
    console.warn('[Pure223] learnResponsePatterns error:', err.message);
    return 0;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// LEARN HANDLER PATTERNS
// ═══════════════════════════════════════════════════════════════════════════════

function learnFromHandlers(forwardFn, encodeFn) {
  ensureInitialized();
  
  if (typeof forwardFn !== 'function' || typeof encodeFn !== 'function') {
    console.warn('[Pure223] learnFromHandlers: invalid functions');
    return 0;
  }

  try {
    for (const [name, data] of Object.entries(HANDLER_PATTERNS)) {
      const vectors = [];
      const weights = [];

      for (const example of data.examples) {
        const vec = safeEncode(encodeFn, forwardFn, example);
        if (!vec) continue;

        const isAnchor = data.anchors.some(a => example.toLowerCase().includes(a));
        vectors.push(vec);
        weights.push(isAnchor ? 2 : 1);
        trainingExamples.handlers.push({ query: example, handler: name, vector: vec });
      }

      if (vectors.length > 0) {
        const centroid = weightedAverageVectors(vectors, weights);
        if (centroid) {
          handlerPatterns.set(name, {
            centroid,
            examples: data.examples,
            anchors: data.anchors,
            responseType: data.responseType
          });
        }
      }

      const negVectors = [];
      for (const neg of (data.negatives || [])) {
        const vec = safeEncode(encodeFn, forwardFn, neg + ' something');
        if (vec) negVectors.push(vec);
      }
      if (negVectors.length > 0) {
        handlerNegatives.set(name, negVectors);
      }
    }

    console.log(`[Pure223] Learned ${handlerPatterns.size} handler patterns`);
    return handlerPatterns.size;
  } catch (err) {
    console.warn('[Pure223] learnFromHandlers error:', err.message);
    return 0;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// LEARN WRITING PATTERNS
// ═══════════════════════════════════════════════════════════════════════════════

function learnWritingPatterns(forwardFn, encodeFn) {
  ensureInitialized();
  
  if (typeof forwardFn !== 'function' || typeof encodeFn !== 'function') return 0;

  try {
    for (const [name, config] of Object.entries(WRITING_PATTERNS)) {
      if (!config.indicators) continue;
      
      const sampleTexts = [];
      for (const indicator of config.indicators) {
        if (indicator.source) {
          const phrase = indicator.source
            .replace(/\\b/g, '')
            .replace(/\|/g, ' ')
            .replace(/[()]/g, '')
            .replace(/\?:/g, '')
            .substring(0, 50);
          sampleTexts.push(phrase);
        }
      }

      const vectors = [];
      for (const text of sampleTexts) {
        const vec = safeEncode(encodeFn, forwardFn, text);
        if (vec) vectors.push(vec);
      }

      if (vectors.length > 0) {
        const centroid = averageVectors(vectors);
        if (centroid) {
          writingPatterns.set(name, { centroid, weight: config.weight || 1 });
        }
      }
    }

    console.log(`[Pure223] Learned ${writingPatterns.size} writing patterns`);
    return writingPatterns.size;
  } catch (err) {
    console.warn('[Pure223] learnWritingPatterns error:', err.message);
    return 0;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// CLASSIFICATION UTILITIES
// ═══════════════════════════════════════════════════════════════════════════════

function classifyEntity(entityVector) {
  ensureInitialized();
  
  try {
    if (!entityVector || !Array.isArray(entityVector) || entityVectors.size === 0) {
      return { type: 'UNKNOWN', confidence: 0 };
    }

    let bestType = 'UNKNOWN', bestScore = -1;
    for (const [type, centroid] of entityVectors) {
      if (!centroid) continue;
      const score = cosineSimilarity(entityVector, centroid);
      if (score > bestScore) {
        bestScore = score;
        bestType = type;
      }
    }
    return { type: bestType, confidence: Math.max(0, bestScore) };
  } catch (e) {
    return { type: 'UNKNOWN', confidence: 0 };
  }
}

function suggestResponsePattern(queryVector) {
  ensureInitialized();
  
  try {
    if (!queryVector || !Array.isArray(queryVector) || responsePatterns.size === 0) {
      return { pattern: 'general', confidence: 0.5 };
    }

    let best = 'general', bestScore = -1;
    for (const [p, centroid] of responsePatterns) {
      if (!centroid) continue;
      const s = cosineSimilarity(queryVector, centroid);
      if (s > bestScore) {
        bestScore = s;
        best = p;
      }
    }
    return { pattern: best, confidence: Math.max(0, bestScore) };
  } catch (e) {
    return { pattern: 'general', confidence: 0.5 };
  }
}

function scoreEvidence(evidenceVector, queryVector) {
  try {
    if (!evidenceVector || !queryVector) return 0;
    const relevance = cosineSimilarity(evidenceVector, queryVector);
    let bonus = 0;

    for (const [, centroid] of responsePatterns) {
      if (!centroid) continue;
      const m = cosineSimilarity(evidenceVector, centroid);
      if (m > 0.6) bonus = Math.max(bonus, m * 0.2);
    }

    return Math.min(1.0, relevance + bonus);
  } catch (e) {
    return 0;
  }
}

function getStructuralSimilarity(queryVector, structuralType) {
  ensureInitialized();
  const results = [];

  try {
    const type = structuralType || 'all';
    
    if ((type === 'all' || type === 'documentType') && structuralEmbeddings.documentType?.vector) {
      results.push({
        type: 'documentType',
        similarity: cosineSim(queryVector, structuralEmbeddings.documentType.vector),
        data: structuralEmbeddings.documentType
      });
    }

    if ((type === 'all' || type === 'hierarchy') && structuralEmbeddings.hierarchy.length > 0) {
      for (const h of structuralEmbeddings.hierarchy) {
        if (h.vector) {
          results.push({
            type: 'hierarchy',
            similarity: cosineSim(queryVector, h.vector),
            data: h.element
          });
        }
      }
    }
  } catch (e) {
    // Return empty results on error
  }

  return results.sort((a, b) => b.similarity - a.similarity);
}

function matchHandlerPattern(queryVector, queryText) {
  ensureInitialized();
  
  try {
    if (!queryVector || !Array.isArray(queryVector) || handlerPatterns.size === 0) {
      return null;
    }

    const qLow = (queryText || '').toLowerCase();
    let bestMatch = null;
    let bestScore = 0.5;

    for (const [name, pattern] of handlerPatterns) {
      if (!pattern || !pattern.centroid) continue;
      
      let sim = cosineSimilarity(queryVector, pattern.centroid);

      if (pattern.anchors) {
        for (const anchor of pattern.anchors) {
          if (qLow.includes(anchor)) {
            sim += 0.2;
            break;
          }
        }
      }

      const negatives = handlerNegatives.get(name);
      if (negatives && negatives.length > 0) {
        let maxNegSim = 0;
        for (const neg of negatives) {
          if (neg) maxNegSim = Math.max(maxNegSim, cosineSimilarity(queryVector, neg));
        }
        sim -= maxNegSim * 0.25;
      }

      if (sim > bestScore) {
        bestScore = sim;
        bestMatch = { name, score: sim, responseType: pattern.responseType };
      }
    }

    return bestMatch;
  } catch (e) {
    return null;
  }
}

function analyzeWritingPatterns(text, forwardFn, encodeFn) {
  try {
    if (!text || typeof forwardFn !== 'function' || typeof encodeFn !== 'function') {
      return { patterns: [], score: 0 };
    }

    const vec = safeEncode(encodeFn, forwardFn, text);
    if (!vec) return { patterns: [], score: 0 };

    const foundPatterns = [];
    let totalScore = 0;
    let totalWeight = 0;

    for (const [name, config] of Object.entries(WRITING_PATTERNS)) {
      let found = false;
      
      if (config.indicators) {
        for (const indicator of config.indicators) {
          try {
            if (indicator.test && indicator.test(text)) {
              found = true;
              break;
            }
          } catch (e) {
            // Skip invalid indicators
          }
        }
      }

      const learned = writingPatterns.get(name);
      if (learned && learned.centroid) {
        const sim = cosineSimilarity(vec, learned.centroid);
        if (sim > 0.5) found = true;
      }

      if (found) {
        const weight = config.weight || 1;
        foundPatterns.push({ name, weight });
        totalScore += weight;
      }
      totalWeight += (config.weight || 1);
    }

    return {
      patterns: foundPatterns,
      score: totalWeight > 0 ? totalScore / totalWeight : 0
    };
  } catch (e) {
    return { patterns: [], score: 0 };
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// ANALYSIS PIPELINE
// ═══════════════════════════════════════════════════════════════════════════════

async function analyze(query, forwardFn, encodeFn) {
  try {
    const vec = safeEncode(encodeFn, forwardFn, query);
    if (!vec) {
      return {
        vector: null,
        intent: { intent: 'general', confidence: 0.5 },
        responsePattern: { pattern: 'general', confidence: 0.5 },
        handlerMatch: null,
        timestamp: Date.now()
      };
    }

    return {
      vector: vec,
      intent: classifyIntent(vec, query),
      responsePattern: suggestResponsePattern(vec),
      handlerMatch: matchHandlerPattern(vec, query),
      entityType: classifyEntity(vec),
      timestamp: Date.now()
    };
  } catch (err) {
    console.warn('[Pure223] analyze error:', err.message);
    return {
      vector: null,
      intent: { intent: 'general', confidence: 0.5 },
      responsePattern: { pattern: 'general', confidence: 0.5 },
      handlerMatch: null,
      timestamp: Date.now()
    };
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// STATISTICS & RESET
// ═══════════════════════════════════════════════════════════════════════════════

function getStats() {
  return {
    intentsLearned: intentVectors.size,
    negativeSamplesLearned: intentNegatives.size,
    entityTypesLearned: entityVectors.size,
    responsePatternsLearned: responsePatterns.size,
    handlerPatterns: handlerPatterns.size,
    writingPatterns: writingPatterns.size,
    structuralEmbeddings: {
      documentType: structuralEmbeddings.documentType ? 1 : 0,
      hierarchy: structuralEmbeddings.hierarchy.length,
      paragraphTypes: structuralEmbeddings.paragraphTypes.size,
      readingLevel: structuralEmbeddings.readingLevel ? 1 : 0
    },
    trainingExamples: {
      intents: trainingExamples.intents.length,
      entities: trainingExamples.entities.length,
      responses: trainingExamples.responses.length,
      structural: trainingExamples.structural.length,
      handlers: trainingExamples.handlers.length,
      writing: trainingExamples.writing.length
    }
  };
}

function reset() {
  intentVectors = new Map();
  intentNegatives = new Map();
  entityVectors = new Map();
  responsePatterns = new Map();
  handlerPatterns = new Map();
  handlerNegatives = new Map();
  writingPatterns = new Map();
  initialized = false;
  
  structuralEmbeddings = {
    documentType: null,
    hierarchy: [],
    paragraphTypes: new Map(),
    readingLevel: null,
    keyPhrases: [],
    topicClusters: []
  };
  
  trainingExamples = {
    intents: [],
    entities: [],
    responses: [],
    evidence: [],
    structural: [],
    writing: [],
    handlers: []
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// EXPORT
// ═══════════════════════════════════════════════════════════════════════════════

const Pure223Intelligence = {
  // Helpers
  averageVectors,
  weightedAverageVectors,
  cosineSimilarity,
  cosineSim,
  normalizeVector,
  euclideanDistance,

  // Q&A Generation
  generateStructuralQA,
  generateLiteraryQA,

  // Learning
  learnStructuralEmbeddings,
  learnIntents,
  learnEntities,
  learnResponsePatterns,
  learnFromHandlers,
  learnWritingPatterns,

  // Classification
  classifyIntent,
  classifyEntity,
  suggestResponsePattern,
  scoreEvidence,
  getStructuralSimilarity,
  matchHandlerPattern,
  analyzeWritingPatterns,

  // Analysis
  analyze,
  getStats,
  reset,

  // State accessors
  get intentVectors() { return intentVectors; },
  get entityVectors() { return entityVectors; },
  get responsePatterns() { return responsePatterns; },
  get structuralEmbeddings() { return structuralEmbeddings; },
  get trainingExamples() { return trainingExamples; },
  get handlerPatterns() { return handlerPatterns; },
  get writingPatterns() { return writingPatterns; },

  // Type lists
  INTENT_TYPES: Object.keys(INTENT_TRAINING_DATA),
  ENTITY_TYPES: Object.keys(ENTITY_PATTERNS),
  RESPONSE_PATTERN_TYPES: Object.keys(RESPONSE_PATTERNS),
  HANDLER_TYPES: Object.keys(HANDLER_PATTERNS),
  WRITING_PATTERN_TYPES: Object.keys(WRITING_PATTERNS)
};

export default Pure223Intelligence;